"use client";

import React from "react";
import Image from "next/image";
import s from "../styles.parts";
import Consts from "@common/config/consts";
import { dayjs, logjs, utils } from "@lib/client";
import { usePartsList } from "../usePartsList";

const log = new logjs("PartsLabelListOrderCompArea");

/**
 * 部品ラベルリスト注文完了表示コンポーネント
 * partslabellist_ordercomp.ctpをReactに移植した表示専用コンポーネント
 *
 * @param {Object} props - プロパティ
 * @param {Object} props.data - データオブジェクト（partsList, request, answer, partskind等を含む）
 * @param {Object} props.qaDialogProps - QAダイアログのプロパティ（open関数を含む）
 * @param {Object} props.qaabDialogProps - 在庫確認（QAAB）ダイアログのプロパティ（open関数を含む）
 * @param {string} props.quote_number - 見積番号
 * @param {Object} props.photoDialogProps - 写真ダイアログのプロパティ（open関数を含む）
 * @param {Object} props.fileupDialogProps - ファイルアップロードダイアログのプロパティ（open関数を含む）
 * @param {Object} props.resPict - 写真データのオブジェクト [PAID] => 写真データ
 * @param {Object} props.resFile - ファイルデータのオブジェクト [PAID] => ファイルデータ
 * @param {Object} props.prMySeiyaku - 自社成約フラグ [PRID] => Hacchu_flg
 * @param {Object} props.prSeiyaku - 成約フラグ [PRID] => 成約フラグ
 * @param {Object} props.AqbData - 在庫確認データ [PAID] => [total, answered, ...]
 * @param {Object} props.nCheck - 発送処理チェックフラグ [PAID] => boolean
 * @returns {JSX.Element} 部品ラベルリスト注文完了表示コンポーネント
 */
export default function PartsLabelListOrderCompArea({
  data,
  state,
  actions,
  editPartsData,
  setEditPartsData,
  qaDialogProps,
  qaabDialogProps,
  quote_number,
  photoDialogProps,
  fileupDialogProps,
  resPict = {},
  resFile = {},
  prMySeiyaku: prMySeiyakuProp = {},
  prSeiyaku: prSeiyakuProp = {},
  AqbData = {},
}) {
  log.debug("START data",data);

  // 共通関数の取得
  const [form, formProps] = usePartsList({ AqbData: data?.AqbData ?? {} }, state, actions);

  const partskind = state?.initData?.partskind || [];

  // data を優先し、なければ props を使う（他画面と揃える）
  const prSeiyaku = data?.prSeiyaku || prSeiyakuProp || {};
  const prMySeiyaku = data?.prMySeiyaku || prMySeiyakuProp || {};

  // getHacchuStatusWithNoneを使用（bgcolor: "none"版）
  const getHacchuStatus = formProps.getHacchuStatusWithNone;

  // 現車と紐付いている場合は色変える
  const isGensha = data?.answer?.[0]?.GenshaID ? true : false;

  // 見積期限が現在時刻以降かどうか
  const isRequestLimitValid = data?.request?.Request_Limit && (dayjs(data.request.Request_Limit).isAfter(dayjs()) || dayjs(data.request.Request_Limit).isSame(dayjs(), "day"));

  // 依頼期限の判定
  const isAfterCarryTaxinDate = formProps.isAfterCarryTaxinDate(data?.request?.Request_Limit);

  if (!data || !data.partsAnswers) {
    return null;
  }

  // 部品行の発送入力を editPartsData に反映する
  const _editPartsData = (paid, index, target_key, value, baseItem = null) => {
    const paidKey = String(paid); // 数値/文字列キーの不一致を防ぐ
    log.debug(`---------- _editPartsData paid:${paidKey} index:${index} target_key:${target_key} value:${value}`);

    setEditPartsData((prev) => {
      const prevObj = prev && typeof prev === "object" && !Array.isArray(prev) ? prev : {};
      const existing = prevObj[paidKey] || {};
      // 初回編集時だけ DB 行をマージし、Haiso_Yotei → Haiso_yotei に寄せる
      const seeded =
        Object.keys(existing).length === 0 && baseItem
          ? {
              ...baseItem,
              Haiso_yotei: baseItem.Haiso_yotei ?? baseItem.Haiso_Yotei ?? null,
            }
          : existing;

      // 日付の空は null に正規化
      const normalizedValue = target_key === "Haiso_yotei" && (value == null || value === "") ? null : value;
      const updatedRow = {
        ...seeded,
        [target_key]: normalizedValue,
      };
      if (target_key === "Haiso_yotei" && normalizedValue) {
        updatedRow.Haiso_Yotei = normalizedValue;
      }

      return {
        ...prevObj,
        [paidKey]: updatedRow,
      };
    });
  };

  const partsAnswers = data?.partsAnswers || [];
  return (
    <>
      {/* ヘッダーセクション */}
      <s.PartsTitle>
        部品情報
        {isRequestLimitValid && (
          <>
            <s.QuestionButton
              data-key={partsAnswers?.[0]?.PRID || ""}
              onClick={(e) => {
                e.preventDefault();
                qaDialogProps.open({
                  MitsumoriNo: quote_number,
                  isQuestion: ( state.user?.Gyoshu_flg === 1),
                  request: data?.request,
                  answer: data?.answer,
                });
              }}
            >
              依頼元へ質問を行う | 回答を見る
            </s.QuestionButton>
            {isGensha && (
              <s.StockedVehicleButton style={{ verticalAlign: "middle", marginTop: "-5px" }} className="important-btn4">
                <Image src="/images/gensha_icon_w.png" alt="入庫済み車輌" width={20} height={20} />
                入庫済みの車輌があります
              </s.StockedVehicleButton>
            )}
          </>
        )}
      </s.PartsTitle>

      {isAfterCarryTaxinDate && <strong style={{ color: "#0000ff", fontSize: "12pt" }}>　※送料は「税別」にて記載してください。</strong>}

      {data?.answer?.[0]?.UserName && (
        <s.FinalAnswererContainer>
          <s.FinalAnswererLabel>最終回答者： {data.answer[0].UserName || ""} 様</s.FinalAnswererLabel>
        </s.FinalAnswererContainer>
      )}

      <s.PLTable>
        <tbody>
          {(() => {
            const rows = [];
            //const partsList = .partsAnswers || [];

            for (let i = 0; i < partsAnswers.length; i++) {
              const partsItem = partsAnswers[i];
              log.debug("partsItem", partsItem);
              const PA_PRID = partsItem?.PRID; // partsItem?.PartsAnswer?.PRID
              const PA_PAID = partsItem?.id; // partsItem?.PartsAnswer?.id
              const isPict = partsItem.isPict;
              const isFile = partsItem.isFile;

              // PA_PRID == 0 かつ StanderdPrice が空の場合はスキップ
              if (PA_PRID === 0 && !partsItem?.StanderdPrice) {
                // partsItem?.PartsAnswer?.StanderdPrice
                continue;
              }

              // 他社成約の判定
              let thCol = 17;
              let thColor = "";
              let showOtherCompany = false;

              if (PA_PRID > 0) {
                const hacchuflg = prMySeiyaku[PA_PRID];
                const seiyaku = prSeiyaku[PA_PRID];
                if (
                  seiyaku &&
                  hacchuflg !== Consts.HACCHU_FLG.KAKUTEI &&
                  hacchuflg !== Consts.HACCHU_FLG.HASOZUMI &&
                  hacchuflg !== Consts.HACCHU_FLG.NEBIKI &&
                  hacchuflg !== Consts.HACCHU_FLG.HENPIN
                ) {
                  thCol = 15;
                  thColor = "border-left-style:none; background:#696969;";
                  showOtherCompany = true;
                }
              }

              const PartsNameHeader = isGensha ? s.PartsNameHeaderGensha : s.PartsNameHeaderNormal;

              // 部品名を取得（発送情報用、グループの最初の部品名を使用）
              // PHPテンプレートでは外側のループで一度設定されて内側のループで使用
              let haiso_pname = "";
              if (PA_PRID === 0) {
                haiso_pname = partsItem?.OthPartsName || ""; // partsItem?.PartsAnswer?.OthPartsName
                if (partsItem?.OthPartsSonota) {
                  // partsItem?.PartsAnswer?.OthPartsSonota
                  haiso_pname += `(${partsItem.OthPartsSonota})`; // partsItem.PartsAnswer.OthPartsSonota
                }
              } else {
                haiso_pname = partsItem?.P_PartsName || "";
              }

              const Bikou = data.partsRList.find((item) => item.id === partsItem.PRID)?.Bikou;

              // 部品名ヘッダー行
              rows.push(
                <React.Fragment key={`header-${i}`}>
                  <tr>
                    {showOtherCompany ? (
                      <s.PartsNameHeaderGensha
                        colSpan="2"
                        scope="row"
                        style={{ borderRightStyle: "none", background: "#696969", textAlign: "center", verticalAlign: "middle" }}
                      >
                        <Image src="/images/tasha.png" alt="他社成約" width={120} height={34} />
                      </s.PartsNameHeaderGensha>
                    ) : null}
                    <PartsNameHeader
                      colSpan={thCol}
                      scope="row"
                      style={
                        thColor
                          ? {
                              borderLeftStyle: "none",
                              background: "#696969",
                            }
                          : undefined
                      }
                    >
                      {PA_PRID === 0 ? (
                        <>
                          {partsItem?.OthPartsName || ""} {/* partsItem?.PartsAnswer?.OthPartsName */}
                          {partsItem?.OthPartsSonota && <>　品名：{partsItem.OthPartsSonota}</>} {/* partsItem?.PartsAnswer?.OthPartsSonota */}
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              marginTop: "5px",
                              gap: "8px",
                            }}
                          >
                            <span>{partsItem?.P_PartsName}</span>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "4px",
                              }}
                            >
                              {/* 部品カテゴリアイコン */}
                              {(() => {
                                let pk3flg = 0;
                                const pkids = [partsItem?.P_PKID1, partsItem?.P_PKID2, partsItem?.P_PKID3, partsItem?.P_PKID4, partsItem?.P_PKID5];

                                return (
                                  <>
                                    {pkids.map((pkid, idx) => {
                                      if (!pkid || pkid === 3) {
                                        if (pkid === 3) pk3flg = 1;
                                        return null;
                                      }
                                      const categoryName = formProps.getCategoryName(pkid, partskind);
                                      const categoryStyle = formProps.getCategoryStyle(pkid);
                                      return (
                                        <s.PartsCategoryIcon key={idx} style={categoryStyle}>
                                          {categoryName}
                                        </s.PartsCategoryIcon>
                                      );
                                    })}
                                    {pk3flg === 1 && (
                                      <>
                                        <span style={{ marginLeft: "10px" }}>　　</span>
                                        <s.PartsCategoryIcon style={formProps.getCategoryStyle(3)}>{formProps.getCategoryName(3, partskind)}</s.PartsCategoryIcon>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                          {Bikou ? <p style={{ margin: "5px 0 0 0", fontSize: "14px", fontWeight: "normal" }}>({Bikou})</p> : <p></p>}
                        </>
                      )}
                    </PartsNameHeader>
                  </tr>

                  {/* テーブルヘッダー行 */}
                  <tr>
                    <s.PLHeadTh2>&nbsp;</s.PLHeadTh2>
                    <s.PLHeadTh2 scope="row">純正品番</s.PLHeadTh2>
                    <s.PLHeadTh2>数量</s.PLHeadTh2>
                    <s.PLHeadTh2>部品カテゴリ</s.PLHeadTh2>
                    <s.PLHeadTh2>価格(税別)</s.PLHeadTh2>
                    {isAfterCarryTaxinDate ? (
                      <s.PLHeadTh2 style={{ backgroundColor: "#FFF666" }}>送料(税別)</s.PLHeadTh2>
                    ) : (
                      <s.PLHeadTh2>
                        送料
                        <span style={{ fontWeight: "bold", backgroundColor: "rgb(255, 255, 0)" }}>(税込)</span>
                      </s.PLHeadTh2>
                    )}
                    <s.PLHeadTh2>発送可能日</s.PLHeadTh2>
                    <s.PLHeadTh2>見積有効期限</s.PLHeadTh2>
                    <s.PLHeadTh2>カラー番号</s.PLHeadTh2>
                    <s.PLHeadTh2>カラー名称</s.PLHeadTh2>
                    <s.PLHeadTh2>写真</s.PLHeadTh2>
                    <s.PLHeadTh2>部品備考</s.PLHeadTh2>
                    <s.PLHeadTh2 scope="row">自社管理番号</s.PLHeadTh2>
                    <s.PLHeadTh2>発送方法</s.PLHeadTh2>
                    <s.PLHeadTh2>運送会社・伝票番号</s.PLHeadTh2>
                    <s.PLHeadTh2>発送日</s.PLHeadTh2>
                    <s.PLHeadTh2>同梱</s.PLHeadTh2>
                  </tr>

                  {/* 回答データ行 */}
                  {(() => {
                    let hasDisplay = false;
                    const dataRows = [];
                    let j = i;

                    for (; j < partsAnswers.length; j++) {
                      const item = partsAnswers[j];
                      const itemPaid = item?.id; // item?.PartsAnswer?.id

                      log.debug("item", item);
                      log.debug("editPartsData", editPartsData);
                      if (item?.StanderdPrice) {
                        // item?.PartsAnswer?.StanderdPrice
                        hasDisplay = true;

                        const paid = item.id; // item.PartsAnswer.id
                        const paidKey = String(paid);
                        // 編集中データ優先、なければ旧キー、なければ DB 行
                        const row = utils.hasOwn(editPartsData, paidKey)
                          ? editPartsData[paidKey]
                          : utils.hasOwn(editPartsData, paid)
                            ? editPartsData[paid]
                            : item;
                        log.debug(`paid:${paidKey} row:`, row);

                        const dataLimit = formProps.getLaterLimit(data?.request?.Request_Limit, row?.Sell_Limit);
                        const btNameQab = formProps.getQabButtonNameWithAqb(paid, dataLimit);
                        const hacchuFlg = item.Hacchu_flg; // item.PartsAnswer.Hacchu_flg
                        const { jyotai, bgcolor, backgroundColor } = getHacchuStatus(hacchuFlg);

                        // 価格データ（partsList[i][0]という構造を想定）
                        // PHPテンプレートでは partsList[$i][0]['PartsPrice'] と partsList[$i][0]['Soryo'] を使用

                        let partsPrice = item?.PartsPrice ? item.PartsPrice : "";
                        let soryo = item?.Soryo ? item.Soryo : 0;

                        // 発送情報の表示内容を取得
                        const renderShippingInfo = () => {
                          // nCheckが存在する場合は発送処理不可メッセージ
                          const nCheck = data?.nCheck || {};
                          if (nCheck[itemPaid]) {
                            return [
                              <s.ConfirmTableCell key="haiso-disabled" colSpan="4" style={{ backgroundColor: "#e5ffe5" }}>
                                現在、発送処理は行えません。
                                <br />
                                少々お待ち下さい。
                              </s.ConfirmTableCell>,
                            ];
                          }
                          log.debug(`hacchuFlg:${hacchuFlg}`);
                          // 注文確定の場合は入力フォーム
                          if (hacchuFlg === Consts.HACCHU_FLG.KAKUTEI) {
                            return [
                              <s.ConfirmTableCell key="haiso-kubun-input" style={{ backgroundColor: "#e5ffe5" }}>
                                {formProps.select_Haiso_kubun({
                                  value: row.Haiso_kubun ?? "",
                                  onChange: (e) => {
                                    _editPartsData(paid, j, "Haiso_kubun", e.target.value, item);
                                  },
                                  style: { width: "100px" },
                                })}
                              </s.ConfirmTableCell>,
                              <s.ConfirmTableCell key="haiso-corp-input" style={{ backgroundColor: "#e5ffe5", textAlign: "left" }}>
                                {formProps.input_Haiso_Corp({
                                  value: row.Haiso_Corp ?? "",
                                  placeholder: "例）西濃運輸",
                                  onChange: (e) => {
                                    _editPartsData(paid, j, "Haiso_Corp", e.target.value, item);
                                  },
                                  style: { width: "150px" },
                                })}
                                <br />
                                {formProps.input_Haiso_denpyo({
                                  value: row.Haiso_denpyo ?? "",
                                  placeholder: "例）12345678",
                                  onChange: (e) => {
                                    _editPartsData(paid, j, "Haiso_denpyo", e.target.value, item);
                                  },
                                  style: { width: "8em" },
                                })}
                              </s.ConfirmTableCell>,
                              <s.ConfirmTableCell key="haiso-yotei-input" style={{ backgroundColor: "#e5ffe5" }}>
                                {formProps.datePicker_Haiso_Yotei({
                                  // 編集キー / DB キーの両方を見る
                                  value: row.Haiso_yotei ?? row.Haiso_Yotei ?? null,
                                  placeholder: "yyyy/mm/dd",
                                  onChange: (value, params) => {
                                    log.debug(`datePicker_Haiso_Yotei onChange value:${value} params:`, params);
                                    // 空は null。item は初回マージ用
                                    _editPartsData(paid, j, "Haiso_yotei", value == null || value === "" ? null : value, item);
                                  },
                                  style: { width: "100px" },
                                })}
                              </s.ConfirmTableCell>,
                              <s.ConfirmTableCell key="haiso-checkbox" style={{ backgroundColor: "#e5ffe5" }}>
                                <div style={{ display: "inline-flex", justifyContent: "center", alignItems: "center" }}>
                                  {formProps.checkbox("check_Hasiso", {
                                    checked: row.check_Hasiso ?? false,
                                    is_standard: true,
                                    onChange: (e) => {
                                      log.debug(`check_Hasiso onChange value:${e.target.checked}`, e.target.checked);
                                      _editPartsData(paid, j, "check_Hasiso", e.target.checked, item);
                                    },
                                    style: { width: "20px", height: "20px" },
                                  })}
                                </div>
                              </s.ConfirmTableCell>,
                            ];
                          }

                          // それ以外の場合は既存データを表示
                          const bgColor = bgcolor || "#C0C0C0";
                          return [
                            <s.ConfirmTableCell key="haiso-kubun" style={{ backgroundColor }}>
                              {formProps.getHaisoKubunText(item?.Haiso_kubun)}
                            </s.ConfirmTableCell>,
                            <s.ConfirmTableCell key="haiso-corp" style={{ backgroundColor: backgroundColor }}>
                              {item?.Haiso_Corp || ""}
                              {item?.Haiso_Corp && item?.Haiso_denpyo && <br />}
                              {item?.Haiso_denpyo || ""}
                            </s.ConfirmTableCell>,
                            <s.ConfirmTableCell key="haiso-yotei" style={{ backgroundColor: backgroundColor }}>
                              {formProps.formatDate(item?.Haiso_Yotei) || ""}
                            </s.ConfirmTableCell>,
                            <s.ConfirmTableCell key="haiso-empty" style={{ backgroundColor: backgroundColor }}></s.ConfirmTableCell>,
                          ];
                        };

                        dataRows.push(
                          <tr key={`data-${j}`} className={bgcolor || undefined}>
                            <s.ConfirmTableCell style={{ backgroundColor }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                {jyotai}
                                {jyotai && <br />}
                                {btNameQab && (
                                  <Image
                                    src={`/images/${btNameQab}.png`}
                                    className="qabOpen"
                                    data-key={`${data.answer?.[0]?.RequestAnswer?.id}/${item?.PRID}`}
                                    style={{ cursor: "pointer" }}
                                    alt="在庫確認"
                                    width={74}
                                    height={64}
                                    onClick={() => {
                                      qaabDialogProps.open({
                                        mitsumoriNo: quote_number,
                                        raid: item?.RAID || null,
                                        paid: paid || null,
                                        isQuestion: state.user?.Gyoshu_flg === 2,
                                      });
                                    }}
                                  />
                                )}
                              </div>
                            </s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }} scope="row">
                              {item?.PartsJyunseiCode || ""}
                            </s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>{item?.Suryo || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>{formProps.getCategoryName(item?.PartsKindID, data.partskind)}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>{partsPrice ? `${formProps.formatNumber(partsPrice)}円` : ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>{soryo === 0 ? "送料込" : soryo ? `${formProps.formatNumber(soryo)}円` : ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>{formProps.getHassoStatusText(item?.Hasso)}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>{formProps.formatDate(item?.Sell_Limit) || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>{item?.ColorNo || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>{item?.ColorName || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor }}>
                              {(() => {
                                const mitsumoriNo = quote_number;
                                const seqnum = partsItem?.Seqnum; // partsItem?.PartsAnswer?.Seqnum
                                const PRID = partsItem?.PRID;

                                if (PA_PRID === 0) {
                                  return (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",}}>
                                      {isPict && (
                                        <s.FileIcon
                                          src="/images/camera_icon2.png"
                                          alt="写真"
                                          onClick={() =>
                                            photoDialogProps.open({
                                              mitsumoriNo: mitsumoriNo,
                                              prid: 0,
                                              seqnum: seqnum,
                                              refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                            })
                                          }
                                        />
                                      )}
                                      {isFile && (
                                        <s.FileIcon
                                          src="/images/file_icon.png"
                                          alt="ファイル"
                                          onClick={formProps.handleFileupClick(fileupDialogProps, mitsumoriNo, 0, seqnum, Consts.WRECKER_PHOTO.YUKO_REF,)}
                                          style={{ cursor: "pointer", }}
                                        />
                                      )}
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",}}>
                                      {isPict && (
                                        <s.FileIcon
                                          src="/images/camera_icon2.png"
                                          alt="写真"
                                          onClick={() =>
                                            photoDialogProps.open({
                                              mitsumoriNo: mitsumoriNo,
                                              prid: PRID,
                                              seqnum: seqnum,
                                              refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                            })
                                          }
                                        />
                                      )}
                                      {isFile && (
                                        <s.FileIcon
                                          src="/images/file_icon.png"
                                          alt="ファイル"
                                          onClick={formProps.handleFileupClick(fileupDialogProps, mitsumoriNo, PRID, seqnum, Consts.WRECKER_PHOTO.YUKO_REF)}
                                          style={{ cursor: "pointer",  }}
                                        />
                                      )}
                                    </div>
                                  );
                                }
                              })()}
                            </s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor, whiteSpace: "pre-line", textAlign: "left" }}>{item?.Bikou || ""}</s.ConfirmTableCell>
                            {bgcolor ? (
                              <s.ConfirmTableCell style={{ backgroundColor }} scope="row">
                                {item?.JishaNo || ""}
                              </s.ConfirmTableCell>
                            ) : (
                              <s.CompanyCodeCellBg style={{ backgroundColor }} scope="row">
                                {item?.JishaNo || ""}
                                <br />
                                {item?.PrepKind === Consts.GOODS.INHOUSE && <label>自社品</label>}
                                {item?.PrepKind === Consts.GOODS.OTHER && <label>他社品</label>}
                              </s.CompanyCodeCellBg>
                            )}
                            {...renderShippingInfo()}
                          </tr>
                        );
                      }

                      // グループの終了判定
                      const nextItem = partsAnswers[j + 1];
                      if (
                        j + 1 >= partsAnswers.length ||
                        (PA_PRID === 0 && item?.OthPartsName !== nextItem?.OthPartsName) || // item?.PartsAnswer?.OthPartsName
                        (PA_PRID !== 0 && PA_PRID !== nextItem?.PRID) // nextItem?.PartsAnswer?.PRID
                      ) {
                        // 未回答行の表示（PHPテンプレートでは17列）
                        if (!hasDisplay && PA_PRID !== 0) {
                          dataRows.push(
                            <s.NoneRow key={`none-${i}`}>
                              <s.ConfirmTableCell scope="row">-</s.ConfirmTableCell>
                              <s.ConfirmTableCell scope="row">-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell scope="row">-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                            </s.NoneRow>
                          );
                        }

                        i = j;
                        break;
                      }
                    }

                    return dataRows;
                  })()}
                </React.Fragment>
              );
            }

            return rows;
          })()}
        </tbody>
      </s.PLTable>
    </>
  );
}
